import { Button, Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi } from "@schemas/issueSchema";
import {
  IconArrowsExchange,
  IconBell,
  IconBellOff,
  IconCopy,
  IconCopyPlus,
  IconExternalLink,
  IconLink,
  IconLock,
  IconLockOpen,
  IconMenu2,
  IconTrash,
} from "@tabler/icons-react";
import { useState } from "react";
import { useHistory } from "react-router";
import {
  openapi,
  usePostProjectByProjectIdIssuesCopyMutation,
  usePutProjectByProjectIdIssueArchiveMutation,
  usePutProjectByProjectIdIssueConvertTypeMutation,
  usePutProjectByProjectIdIssueSubscribeMutation,
} from "../../../../../../../openapi";
import ConnectExistingIssueToItemModalWrapper from "../../../../../../app/ConnectExistingIssueToItemModalWrapper";
import { ConfirmAbortModal } from "../../../../../../components/ConfirmAbortModal/ConfirmAbortModal";
import { removeAppModalInRedux, selectUserMember } from "../../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import { useToggleClientAccessMutation } from "../../../issueRTK";
import { selectIssueRegisterNameOptionOnBoard, selectTaskRegisterNameOptionOnBoard } from "../../../issueSelectors";
import { issueBoardHasClientAccess } from "../../../issueUtils";
import IssueChangeRegisterSelectorSubMenu from "./IssueChangeItemRegisterSelector";

type IssueItemModalMenuProps = {
  issue: IIssueApi;
  projectId: string;
  issueBoard: IIssueBoardApi;
};

const lowerFirst = (value: string): string => value.charAt(0).toLocaleLowerCase() + value.slice(1);

const IssueItemModalMenu = ({ issue, projectId, issueBoard }: IssueItemModalMenuProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openConvertToIssueConfirm, setOpenConvertToIssueConfirm] = useState<boolean>(false);
  const [openParentIssueSelector, setOpenParentIssueSelector] = useState<boolean>(false);
  const user = useAppSelector((state) => state.globalReducer.user);
  const hasSubscription = issue.subscribers?.indexOf(user?._id) > -1;

  const [archiveIssue] = usePutProjectByProjectIdIssueArchiveMutation();
  const [toggleSubscription] = usePutProjectByProjectIdIssueSubscribeMutation();
  const [postIssueCopy] = usePostProjectByProjectIdIssuesCopyMutation();
  const [convertIssueType, { isLoading: isConverting }] = usePutProjectByProjectIdIssueConvertTypeMutation();
  const [toggleClientAccess] = useToggleClientAccessMutation();

  const userMember = useAppSelector(selectUserMember);
  const issueNameOption = useAppSelector(selectIssueRegisterNameOptionOnBoard);
  const taskNameOption = useAppSelector(selectTaskRegisterNameOptionOnBoard);
  const boardHasClientAccess = issueBoardHasClientAccess(issueBoard);
  const canToggleClientAccess = userMember?.group !== "CLIENT" && boardHasClientAccess;
  const isLockedForCustomers = issue.clientAccess !== true;
  const issueName = {
    singularLower: lowerFirst(issueNameOption?.singular ?? "Sak"),
    definite: issueNameOption?.definite ?? "Saken",
    definiteLower: lowerFirst(issueNameOption?.definite ?? "Saken"),
    register: issueNameOption?.register ?? "Saksregister",
  };
  const taskName = {
    singularLower: lowerFirst(taskNameOption?.singular ?? "Aksjon"),
    definite: taskNameOption?.definite ?? "Aksjonen",
    pluralLower: lowerFirst(taskNameOption?.plural ?? "Aksjoner"),
  };
  const connectedTaskCount = issue.connectedTasks?.length ?? 0;
  const connectedTaskMoveText =
    connectedTaskCount === 0
      ? ``
      : connectedTaskCount === 1
      ? `1 ${taskName.singularLower} flyttes med til samme sak.`
      : `${connectedTaskCount} ${taskName.pluralLower} flyttes med til samme sak.`;
  const requiresConvertTaskConfirmation = connectedTaskCount > 3;

  const copyIssue = async (includeTasks: boolean) => {
    await postIssueCopy({
      projectId,
      body: {
        issueId: issue._id,
        includeTasks,
      },
    });

    notifications.show({
      title: "Kopi opprettet",
      message: includeTasks
        ? `En kopi av ${issueName.definiteLower} med ${taskName.pluralLower} er nå opprettet.`
        : `En kopi av ${issueName.definiteLower} er nå opprettet.`,
      color: "green",
      icon: <IconCopy size={16} />,
    });
  };

  const toggleSubscribe = async () => {
    await toggleSubscription({ projectId, body: { issueId: issue._id } });

    notifications.show({
      icon: hasSubscription ? <IconBellOff size={16} /> : <IconBell size={16} />,
      title: hasSubscription ? "Abonnement fjernet" : "Abonnement opprettet",
      message: hasSubscription
        ? `Du vil ikke lenger motta varsler for ${issueName.definiteLower}.`
        : `Du vil nå motta varsler for ${issueName.definiteLower}.`,
      color: "green",
    });
  };

  const toggleCustomerAccess = async () => {
    await toggleClientAccess({ projectId, issueId: issue._id });
    dispatch(openapi.util.invalidateTags(["Issue", "Issues"]));

    notifications.show({
      icon: isLockedForCustomers ? <IconLockOpen size={16} /> : <IconLock size={16} />,
      title: isLockedForCustomers ? "Kundetilgang åpnet" : "Låst for kunder",
      message: isLockedForCustomers
        ? `Kunder har nå tilgang til ${issueName.definiteLower}.`
        : `Kunder har ikke lenger tilgang til ${issueName.definiteLower}.`,
      color: "green",
    });
  };

  const archive = async () => {
    await archiveIssue({
      body: {
        issueId: issue._id,
      },
      projectId,
    });

    dispatch(removeAppModalInRedux(issue._id));
    history.replace(`${history.location.pathname}`);

    notifications.show({
      title: "Slettet",
      message: `${issueName.definite} er nå slettet.`,
      color: "green",
      icon: <IconTrash size={16} />,
    });
  };

  const convertToTask = async (connectedIssueId: string) => {
    await convertIssueType({
      projectId,
      body: {
        issueId: issue._id,
        taskOrIssue: "TASK",
        connectedIssueId,
      },
    }).unwrap();

    setOpenParentIssueSelector(false);
    notifications.show({
      title: "Type endret",
      message: `${issueName.definite} er endret til ${taskName.singularLower}.`,
      color: "green",
      icon: <IconArrowsExchange size={16} />,
    });
  };

  const convertToIssue = async () => {
    await convertIssueType({
      projectId,
      body: {
        issueId: issue._id,
        taskOrIssue: "ISSUE",
        connectedIssueId: null,
      },
    }).unwrap();

    setOpenConvertToIssueConfirm(false);
    notifications.show({
      title: "Type endret",
      message: `${taskName.definite} er endret til ${issueName.singularLower}.`,
      color: "green",
      icon: <IconArrowsExchange size={16} />,
    });
  };

  const currentUrl = window.location.href;
  const issueUrl = `${window.location.origin}/#/project/${projectId}/issues/${issue.issueBoard._id}?id=${issue._id}`;

  return (
    <>
      <ConfirmAbortModal
        isOpen={openConfirm}
        onCancel={() => setOpenConfirm(false)}
        onConfirm={async () => {
          await archive();
          setOpenConfirm(false);
        }}
        customHeader="Bekreft sletting"
        customText={`Er du sikker på at du vil slette ${issueName.definiteLower}?`}
        customButtonText="Bekreft"
      />
      <ConfirmAbortModal
        isOpen={openConvertToIssueConfirm}
        onCancel={() => setOpenConvertToIssueConfirm(false)}
        onConfirm={async () => {
          await convertToIssue();
        }}
        customHeader={`Endre til ${issueName.singularLower}`}
        customText={`${taskName.definite} fjernes fra ${issueName.definiteLower} den er koblet til.`}
        customButtonText="Bekreft"
        customButtonIcon={null}
        customButtonVariant="filled"
      />
      <ConnectExistingIssueToItemModalWrapper
        opened={openParentIssueSelector}
        projectId={projectId}
        defaultIssueBoard={{ _id: issue.issueBoard._id, name: issue.issueBoard.name }}
        onConnected={async (_boardId, connectedIssueId) => {
          await convertToTask(connectedIssueId);
        }}
        closeModal={() => setOpenParentIssueSelector(false)}
        blockedIssueIds={[issue._id, ...((issue.connectedTasks ?? []) as IIssueApi[]).map((task) => task._id)]}
        allowedTypes={["ISSUE"]}
        description={`Velg hvilken sak ${issue.title ?? issueName.definiteLower} skal kobles til som ${
          taskName.singularLower
        }. ${connectedTaskMoveText}`}
        requireConfirmation={requiresConvertTaskConfirmation}
        confirmationLabel={`Jeg bekrefter at ${connectedTaskCount} ${taskName.pluralLower} skal flyttes med til valgt sak.`}
      />
      <Menu position="bottom-start">
        <Menu.Target>
          <Button variant="light">
            <IconMenu2 size={16} />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {hasSubscription === true ? (
            <Menu.Item leftSection={<IconBellOff size={16} />} onClick={() => toggleSubscribe()}>
              Fjern abonnement
            </Menu.Item>
          ) : (
            <Menu.Item leftSection={<IconBell size={16} />} onClick={() => toggleSubscribe()}>
              Abonner
            </Menu.Item>
          )}
          <Menu.Item
            onClick={async () => {
              await navigator.clipboard.writeText(issueUrl);

              notifications.show({
                title: "Lenke kopiert",
                icon: <IconLink size={16} />,
                message: `Lenken til ${issueName.definiteLower} er nå kopiert til utklippstavlen.`,
                color: "green",
              });
            }}
            leftSection={<IconLink size={16} />}
          >
            Kopier lenke
          </Menu.Item>
          <Menu.Item
            disabled={currentUrl === issueUrl}
            onClick={() => {
              history.push(`/project/${projectId}/issues/${issue.issueBoard._id}?id=${issue._id}`);
            }}
            leftSection={<IconExternalLink size={16} />}
          >
            Gå til {issueName.register}
          </Menu.Item>
          <IssueChangeRegisterSelectorSubMenu issue={issue} projectId={projectId} activeBoard={issue.issueBoard} />
          {canToggleClientAccess && (
            <Menu.Item
              onClick={() => toggleCustomerAccess()}
              leftSection={isLockedForCustomers ? <IconLockOpen size={16} /> : <IconLock size={16} />}
            >
              {isLockedForCustomers ? "Åpne for kunder" : "Lås for kunder"}
            </Menu.Item>
          )}
          <Menu.Divider />
          {issue.taskOrIssue === "ISSUE" ? (
            <Menu.Item
              disabled={isConverting}
              onClick={() => setOpenParentIssueSelector(true)}
              leftSection={<IconArrowsExchange size={16} />}
            >
              Endre til {taskName.singularLower}
            </Menu.Item>
          ) : (
            <Menu.Item
              disabled={isConverting}
              onClick={() => setOpenConvertToIssueConfirm(true)}
              leftSection={<IconArrowsExchange size={16} />}
            >
              Endre til {issueName.singularLower}
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item onClick={() => copyIssue(false)} leftSection={<IconCopy size={16} />}>
            Lag kopi
          </Menu.Item>
          <Menu.Item onClick={() => copyIssue(true)} leftSection={<IconCopyPlus size={16} />}>
            Lag kopi med {taskName.pluralLower}
          </Menu.Item>
          {issueBoard?.disableDeleteIssue !== true && (
            <>
              <Menu.Divider />
              <Menu.Item
                onClick={() => {
                  setOpenConfirm(true);
                }}
                color="red"
                leftSection={<IconTrash size={16} />}
              >
                Slett
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

export default IssueItemModalMenu;
